import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { AlertCircle, Clock, Phone, Mail } from 'lucide-react';

const SubscriptionReminder = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [dismissedToday, setDismissedToday] = useState(false);

  useEffect(() => {
    if (user && user.role === 'provider' && user.approved) {
      loadSubscription();
    }
  }, [user]);

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/${user._id}`, { withCredentials: true });
      setSubscription(response.data);

      // Check if we should show modal
      const lastDismissed = localStorage.getItem(`subscription_dismissed_${user._id}`);
      const today = new Date().toISOString().split('T')[0];
      const shouldShow = response.data.needs_reminder || response.data.is_expired;
      
      if (shouldShow && lastDismissed !== today) {
        setShowModal(true);
      } else if (shouldShow) {
        setDismissedToday(true);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const handleDismiss = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(`subscription_dismissed_${user._id}`, today);
    setShowModal(false);
    setDismissedToday(true);
  };

  if (!subscription || (!subscription.needs_reminder && !subscription.is_expired)) {
    return null;
  }

  return (
    <>
      {/* Persistent Notification Bar */}
      {(subscription.needs_reminder || subscription.is_expired) && (
        <div
          data-testid="subscription-reminder-bar"
          className={`sticky top-0 z-40 px-4 py-2.5 flex items-center justify-center gap-3 border-b ${
            subscription.is_expired
              ? 'bg-red-500/20 border-red-500/30'
              : 'bg-yellow-500/20 border-yellow-500/30'
          }`}
        >
          <AlertCircle className={`w-4 h-4 ${subscription.is_expired ? 'text-red-400' : 'text-yellow-400'}`} />
          <p className={`text-sm ${subscription.is_expired ? 'text-red-300' : 'text-yellow-300'}`}>
            {subscription.is_expired
              ? '⚠️ Tu suscripción ha expirado. Contacta al administrador para renovar.'
              : `⏰ Tu suscripción vence en ${subscription.days_remaining} día${subscription.days_remaining !== 1 ? 's' : ''}. Contacta al administrador.`}
          </p>
          {dismissedToday && (
            <button
              data-testid="subscription-show-modal"
              onClick={() => setShowModal(true)}
              className="text-xs underline opacity-70 hover:opacity-100"
            >
              Ver detalles
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent
          data-testid="subscription-reminder-modal"
          className="bg-[#0B0C10] border-white/10 text-white max-w-md rounded-3xl"
        >
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-xl ${subscription.is_expired ? 'bg-red-500/20' : 'bg-yellow-500/20'}`}>
                <Clock className={`w-5 h-5 ${subscription.is_expired ? 'text-red-400' : 'text-yellow-400'}`} />
              </div>
              <DialogTitle className="text-xl font-medium text-white" style={{ fontFamily: 'Outfit' }}>
                {subscription.is_expired ? 'Suscripción Expirada' : 'Recordatorio de Pago'}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className={`p-4 rounded-2xl border ${
              subscription.is_expired
                ? 'bg-red-500/10 border-red-500/20'
                : 'bg-yellow-500/10 border-yellow-500/20'
            }`}>
              <p className="text-white font-medium mb-2">
                {subscription.is_expired
                  ? 'Tu suscripción ha expirado'
                  : `Tu suscripción vence en ${subscription.days_remaining} día${subscription.days_remaining !== 1 ? 's' : ''}`}
              </p>
              <p className="text-sm text-[#9CA3AF]">
                {subscription.is_expired
                  ? 'Tu cuenta ha sido desactivada. Para reactivarla, por favor contacta al administrador y realiza el pago correspondiente.'
                  : 'Para evitar la interrupción del servicio, por favor contacta al administrador para renovar tu suscripción.'}
              </p>
            </div>

            <div className="glass-card p-4 rounded-2xl">
              <p className="text-xs text-[#9CA3AF] uppercase tracking-widest mb-3">Contacta al administrador</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-white">
                  <Mail className="w-4 h-4 text-[#8A2BE2]" />
                  admin@provider.com
                </div>
                <div className="flex items-center gap-2 text-sm text-white">
                  <Phone className="w-4 h-4 text-[#FF007F]" />
                  Solicita el número por email
                </div>
              </div>
            </div>

            <Button
              data-testid="subscription-dismiss-button"
              onClick={handleDismiss}
              className="w-full gradient-button rounded-xl py-5"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SubscriptionReminder;
